/*
 * Profile wizard — pure model (gating, validation-banner copy, phone engine,
 * done-screen summary). Transcribed from the App-shell canon §5.1–§5.6
 * (needByStep / okStep / summary in the DC source). No IO here — unit-tested.
 *
 * SELF SUBJECT ONLY this cycle. The managed-child variant (subject switch,
 * age-band gate, junior sizes, guardian prefills) is deferred to the expected
 * V1.1 kids canon amendment — see TODO(V1.1 kids amendment §12.2) seams.
 */
import { KEEPS_TRUNK_ZERO } from '../../auth/cognitoProfile'
import { translate, type MessageKey, type TranslateFn } from '../../i18n/useLocale'
import { countryByDial, countryByIso, REF_COUNTRIES, type RefCountry } from './referenceData'
import type { WizardEventFields } from './wizardConfig'

/** Default resolver — English, so the pure functions stay directly assertable
 *  in unit tests; the screen passes the live locale's t. */
const enT: TranslateFn = (k, p) => translate('en', k, p)

// ---------------------------------------------------------------------------
// Draft record — every field the self wizard reads/writes
// ---------------------------------------------------------------------------

export interface WizardDraft {
  first: string
  last: string
  dobD: string
  dobM: string
  dobY: string
  /** Picked gender value only (canon §11.10 — never free text)… */
  gender: string
  /** …except the flagged-policy self-describe free text (canon §5.2). */
  selfText: string
  /** Nationality demonym — set ONLY by a typeahead pick. */
  nationality: string
  natIso: string
  resCountry: string
  resIso: string
  /** City — valid only when picked from the residence country's list. */
  city: string
  addr: string
  /** Dial select value `{ISO} +{dial}`, e.g. `AE +971`. */
  dial: string
  /** National number as typed (digits/spaces/dashes; trunk 0 stripped live). */
  phone: string
  ecName: string
  ecDial: string
  ecPhone: string
  ecRel: string
  language: string
  shirt: string
}

export const EMPTY_DRAFT: WizardDraft = {
  first: '', last: '', dobD: '', dobM: '', dobY: '', gender: '', selfText: '',
  nationality: '', natIso: '', resCountry: '', resIso: '', city: '', addr: '',
  dial: '', phone: '', ecName: '', ecDial: '', ecPhone: '', ecRel: '',
  language: '', shirt: '',
}

// ---------------------------------------------------------------------------
// Fixed option sets (canon §5.2 / §5.4 / §5.5 — verbatim)
// ---------------------------------------------------------------------------

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1))

/** Self year range: 2012 down to 1940, 73 entries (canon §5.2 — "a child's
 *  birth year can be recent; an adult entrant's cannot"). */
export const SELF_YEARS = Array.from({ length: 73 }, (_, i) => String(2012 - i))

/** Child year range: current year down, 22 entries (a child's birth year CAN
 *  be recent — the DC anchors on the current year). */
export const CHILD_YEARS = Array.from({ length: 22 }, (_, i) => String(new Date().getFullYear() - i))

export const RELATIONSHIPS = ['Partner', 'Parent', 'Child', 'Sibling', 'Friend']

export const LANGUAGES = ['English', 'العربية', 'Français', 'Español', 'Deutsch', 'Italiano', 'Português', '中文']

export const SELF_SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL']

/** Junior sizes (child subject) — age-suggested, always overridable. */
export const JUNIOR_SHIRT_SIZES = ['3-4y', '5-6y', '7-8y', '9-10y', '11-12y', '13-14y']

/** Whole-year age from the explicit DOB selects; null while incomplete. */
export function ageFromDob(d: Pick<WizardDraft, 'dobD' | 'dobM' | 'dobY'>, at: Date = new Date()): number | null {
  if (!d.dobD || !d.dobM || !d.dobY) return null
  const mi = MONTHS.indexOf(d.dobM)
  if (mi < 0) return null
  const b = new Date(Number(d.dobY), mi, Number(d.dobD))
  return at.getFullYear() - b.getFullYear()
    - ((at.getMonth() < b.getMonth() || (at.getMonth() === b.getMonth() && at.getDate() < b.getDate())) ? 1 : 0)
}

/** Age-suggested junior size (DC: `[..sizes][clamp(floor((age-3)/2), 0, 5)]`);
 *  null when the age is unknown — never a fabricated suggestion. */
export function suggestedJuniorShirt(age: number | null): string | null {
  if (age === null) return null
  return JUNIOR_SHIRT_SIZES[Math.max(0, Math.min(5, Math.floor((age - 3) / 2)))]
}

/** Gender pill sets + helpbox copy per policy (canon §5.2 — verbatim). The
 *  helpbox resolves through the i18n resource (`{org}` = organiser name):
 *  `helpKey` replaced the P3 hardcoded-English `help(org)` closures that left
 *  the `wizard.help.gender.*` keys dead (P5 review follow-up). The flagged
 *  policy shares the binary copy (canon §5.2 — same sentence). */
export const GENDER_MODES = {
  binary: {
    opts: ['Female', 'Male'],
    helpKey: 'wizard.help.gender.binary',
  },
  flagged: {
    opts: ['Female', 'Male', 'Prefer to self-describe'],
    helpKey: 'wizard.help.gender.binary',
  },
  third: {
    opts: ['Women', 'Men', 'Open'],
    helpKey: 'wizard.help.gender.third',
  },
} as const satisfies Record<string, { opts: readonly string[]; helpKey: MessageKey }>

export const SELF_DESCRIBE_PILL = 'Prefer to self-describe'

// ---------------------------------------------------------------------------
// Phone engine (canon §5.3 — dial + national number, E.164 rules), BACKED BY
// libphonenumber-js (V1.9.1 README Open-questions #2: "back this with
// libphonenumber"). The library's possible-length metadata is the validity
// authority; the expected-count hint uses its example-number machinery, with
// the curated referenceData min/max as the display range for launch markets.
// Reuses the shipped trunk-zero prior art: KEEPS_TRUNK_ZERO from
// src/auth/cognitoProfile.ts (['+39'] — Italy keeps its leading 0).
// ---------------------------------------------------------------------------
import { getExampleNumber, isValidPhoneNumber, type CountryCode } from 'libphonenumber-js'
import phoneExamples from 'libphonenumber-js/mobile/examples'

/** Launch-market (UAE) default dial. DERIVED from the AE reference entry so it
 *  is ALWAYS the exact `{ISO} +{dial}` string dialOptions() emits for AE
 *  (BOARDROOM-2026-08-29-032706 F2): a hardcoded default that isn't a literal
 *  member of the option list makes the browser silently fall back to option[0]
 *  (the alphabetically-first country), which is how the dial showed the wrong
 *  country. The literal is kept only as a build-time-safe fallback. */
export const DEFAULT_DIAL = ((): string => {
  const ae = countryByIso('AE')
  return ae ? `${ae.iso} +${ae.d}` : 'AE +971'
})()

/** `AE +971` → `971`; tolerates a bare `971` (DC). */
export const dialOf = (label: string): string =>
  (String(label || '').includes('+') ? String(label).split('+')[1] : String(label || '')).trim()

/** `AE +971` → `AE`; null for a bare dial (fallback: countryByDial). */
const isoOfLabel = (label: string): string | null => {
  const m = /^([A-Za-z]{2})\s*\+/.exec(String(label || '').trim())
  return m ? m[1].toUpperCase() : null
}

const digitsOf = (v: string): string => (v || '').replace(/\D/g, '')

const keepsTrunkZero = (dial: string): boolean => KEEPS_TRUNK_ZERO.includes(`+${dial}`)

const PERMISSIVE_RULE = { min: 4, max: 15 }

const ruleFor = (dial: string): { min: number; max: number } =>
  countryByDial(dial) ?? { min: 6, max: 14 }

/** Expected national-number digit count for the hint line, via the library's
 *  example-number machinery; null when the region has no example. */
export function expectedNationalDigits(iso: string | null): number | null {
  if (!iso) return null
  try {
    const ex = getExampleNumber(iso as CountryCode, phoneExamples)
    return ex ? String(ex.nationalNumber).length : null
  } catch {
    return null
  }
}

/** Library validity for the typed national digits — STRICT pattern validity
 *  (isValidPhoneNumber), not bare possible-length (several regions carry short
 *  service-number lengths in the metadata that would wave a half-typed mobile
 *  through). null when the region is unknown to the library — callers fall
 *  back to the curated digit rules. */
function libraryValid(iso: string | null, nationalDigits: string): boolean | null {
  if (!iso || !nationalDigits) return null
  try {
    return isValidPhoneNumber(nationalDigits, iso as CountryCode)
  } catch {
    return null
  }
}

export interface PhoneState {
  dial: string
  /** Normalised national digits (trunk zero stripped unless kept). */
  digits: string
  ok: boolean
  touched: boolean
  strippedZero: boolean
  /** Hint line — verbatim canon templates. */
  hint: string
  tone: 'idle' | 'valid' | 'invalid'
}

/** Pure phone validation per the canon §5.3 rules, libphonenumber-backed.
 *  `raw` is the input value. Validity comes from the library's possible-length
 *  metadata when the region is known; the curated referenceData digit rules
 *  remain the fallback (and the idle-hint display range for launch markets).
 *  Hint copy resolves through the `wizard.phone.*` i18n keys (P5 review
 *  follow-up — they sat dead while the templates were hardcoded here); the
 *  English default keeps the pure function directly assertable in unit tests,
 *  the screen passes the live locale's t. */
export function phoneState(dialLabel: string, raw: string, tr: TranslateFn = enT): PhoneState {
  const label = dialLabel || DEFAULT_DIAL
  const dial = dialOf(label)
  const iso = isoOfLabel(label) ?? countryByDial(dial)?.iso ?? null
  const r = ruleFor(dial)
  const rawDigits = digitsOf(raw)
  const dg = keepsTrunkZero(dial) ? rawDigits : rawDigits.replace(/^0+/, '')
  const strippedZero = /^0/.test(rawDigits) && !keepsTrunkZero(dial)
  const valid = libraryValid(iso, dg)
  const ok = valid !== null ? valid : dg.length >= r.min && dg.length <= r.max
  const touched = dg.length > 0 || /^0+$/.test(rawDigits)
  // Expected-count display: the curated range when this is a launch market
  // with precise rules; else the library's example-number length; else the
  // permissive fallback range.
  const curated = !(r.min === PERMISSIVE_RULE.min && r.max === PERMISSIVE_RULE.max)
  const example = expectedNationalDigits(iso)
  const need = curated
    ? (r.min === r.max ? String(r.min) : `${r.min}–${r.max}`)
    : example !== null ? String(example) : `${r.min}–${r.max}`
  // A count message may only claim the count is the problem. When the library
  // rejected a number whose LENGTH is already plausible, saying "Needs 9
  // digits — you have 9" is self-contradictory — name the real failure.
  const lengthPlausible = curated
    ? dg.length >= r.min && dg.length <= r.max
    : example !== null
      ? dg.length === example
      : dg.length >= r.min && dg.length <= r.max
  const hint = !touched
    ? tr('wizard.phone.hintIdle', { need })
    : strippedZero
      ? tr('wizard.phone.stripped')
      : ok
        ? tr('wizard.phone.hintOk')
        : valid === false && lengthPlausible
          ? tr('wizard.phone.hintInvalid')
          : tr('wizard.phone.hintShort', { need, have: dg.length })
  return {
    dial, digits: dg, ok, touched, strippedZero, hint,
    tone: !touched ? 'idle' : ok ? 'valid' : 'invalid',
  }
}

/** Live input filter: keep digits/spaces/dashes; strip a pasted leading trunk
 *  zero to E.164 as they type (canon §5.3; KEEPS_TRUNK_ZERO exception).
 *  `stripped` drives the verbatim `Leading 0 removed — numbers are stored
 *  international` note (canon acceptance: the note shows on the paste even
 *  though the stored value no longer carries the zero). */
export function filterPhoneInput(
  dialLabel: string,
  value: string,
): { value: string; stripped: boolean } {
  const dial = dialOf(dialLabel || DEFAULT_DIAL)
  const cleaned = value.replace(/[^\d\s-]/g, '')
  const d = digitsOf(cleaned)
  const stripped = !keepsTrunkZero(dial) && /^0/.test(d)
  return { value: stripped ? d.replace(/^0+/, '') : cleaned, stripped }
}

/** Stored value is E.164: dial + national number (canon §5.3 acceptance). */
export function toE164(dialLabel: string, raw: string): string {
  const st = phoneState(dialLabel, raw)
  return st.digits ? `+${st.dial}${st.digits}` : ''
}

/** Parse a stored E.164 back into {dialLabel, national} using the reference
 *  list (longest dial-prefix match); null if unparseable. */
export function fromE164(e164: string): { dialLabel: string; national: string } | null {
  if (!e164 || !e164.startsWith('+')) return null
  const digits = e164.slice(1)
  // longest-prefix match over the reference dials
  let best: RefCountry | null = null
  for (const c of REF_COUNTRIES) {
    if (digits.startsWith(c.d) && (!best || c.d.length > best.d.length)) best = c
  }
  if (!best) return null
  return { dialLabel: `${best.iso} +${best.d}`, national: digits.slice(best.d.length) }
}

// ---------------------------------------------------------------------------
// Derived facts
// ---------------------------------------------------------------------------

export const dobFull = (d: WizardDraft): string =>
  d.dobD && d.dobM && d.dobY ? `${d.dobD} ${d.dobM} ${d.dobY}` : ''

/** The draft's DOB as ISO `YYYY-MM-DD` (the Cognito `birthdate` shape the
 *  eligibility gate consumes — same construction profileStore.saveSelfDraft
 *  persists), or '' while incomplete. Never a guessed date. */
export const dobIso = (d: Pick<WizardDraft, 'dobD' | 'dobM' | 'dobY'>): string => {
  if (!d.dobD || !d.dobM || !d.dobY) return ''
  const mi = MONTHS.indexOf(d.dobM)
  if (mi < 0) return ''
  return `${d.dobY.padStart(4, '0')}-${String(mi + 1).padStart(2, '0')}-${d.dobD.padStart(2, '0')}`
}

export const genderOk = (d: WizardDraft, policy: 'binary' | 'flagged' | 'third'): boolean =>
  !!d.gender && !(policy === 'flagged' && d.gender === SELF_DESCRIBE_PILL && !d.selfText)

export const natSelected = (d: WizardDraft): RefCountry | null =>
  d.natIso ? countryByIso(d.natIso) : null

export const resSelected = (d: WizardDraft): RefCountry | null =>
  d.resIso ? countryByIso(d.resIso) : null

/** City is valid once a residence is chosen and a city is SET. `draft.city` is
 *  only ever written by a deliberate typeahead pick (a real city or the explicit
 *  "use my entry" free-text option) — free typing clears it — so a set value is
 *  always an intentional choice. This replaced the old
 *  `rc.c.includes(d.city)` allow-list, which silently blocked any city outside
 *  the tiny per-country seed (CMT-100427: e.g. Ayutthaya in Thailand). */
export const cityValid = (d: WizardDraft): boolean => {
  const rc = resSelected(d)
  return !!(rc && d.city)
}

// ---------------------------------------------------------------------------
// Step gates + validation-banner copy (canon §5 needByStep — verbatim phrases)
// ---------------------------------------------------------------------------

export interface GateContext {
  genderPolicy: 'binary' | 'flagged' | 'third'
  eventFields: WizardEventFields
  /** 'self' (default) or 'child' — switches the need phrasing (README §Flows B
   *  child variants) and which fields gate (a child has no phone/language). */
  subject?: 'self' | 'child'
  /** Age-suggested junior size (child) — an untouched shirt with a live
   *  suggestion gates as satisfied (DC effShirt = P.shirt || suggested). */
  suggestedShirt?: string | null
  /** Event-level question toggles (CMT-2026-08-29-022): false ⇒ the question
   *  never rendered, so it can NEVER be required to advance and never shows a
   *  summary row. Absent ⇒ asked (today's behaviour — fail-toward-asking). */
  askLanguage?: boolean
  askShirt?: boolean
  /** Identity-field edit lock (PROGRAMME-RULE-IDENTITY-FIELD-EDIT-LOCK-BY-
   *  VERIFICATION-LEVEL-V1): true when the SELF subject's verification level
   *  is <= 2 (event-ID-verified or above). The identity-core set on this
   *  surface — name (step 1) + DOB/gender (step 2) — renders read-only, and
   *  those fields COUNT AS ANSWERED for gating (they are, by definition,
   *  verified server-side; a locked field must never dead-end the wizard).
   *  Absent/false ⇒ level 3 self-attested — today's behaviour. */
  identityLocked?: boolean
}

/** Missing-item phrases for a step (1-based) — self/child variants per the
 *  README (child: `the date of birth`, `a category`, `a T-shirt size`).
 *  Phrases resolve through the i18n resource (QA §9.7); English default so the
 *  pure tests assert directly. */
export function stepNeeds(step: number, d: WizardDraft, ctx: GateContext, tr: TranslateFn = enT): string[] {
  const F = ctx.eventFields
  const child = ctx.subject === 'child'
  const ph = phoneState(d.dial, d.phone)
  const phoneOkOrEmpty = !ph.touched || ph.ok
  const ecp = phoneState(d.ecDial, d.ecPhone)
  const effShirt = child ? (d.shirt || ctx.suggestedShirt || '') : d.shirt
  // Locked identity fields count as answered (see GateContext.identityLocked):
  // steps 1–2 can never block, so an L2 participant always reaches — and can
  // complete — the still-editable steps.
  const idLocked = ctx.identityLocked === true
  const byStep: Array<Array<string | false>> = [
    [
      !idLocked && !d.first && tr('wizard.need.first'),
      !idLocked && !d.last && tr('wizard.need.last'),
    ],
    [
      !idLocked && !dobFull(d) && tr(child ? 'wizard.need.dob.child' : 'wizard.need.dob.self'),
      !idLocked && !genderOk(d, ctx.genderPolicy) && tr(child ? 'wizard.need.gender.child' : 'wizard.need.gender.self'),
    ],
    [
      !natSelected(d) && tr('wizard.need.nationality'),
      !resSelected(d) && tr('wizard.need.residence'),
      !cityValid(d) && tr('wizard.need.city'),
      // Phone is the account holder's field — never asked of a child subject.
      !child && !!F.phone && !phoneOkOrEmpty && tr('wizard.need.phoneValid'),
      !child && F.phone === 'required' && !ph.touched && tr('wizard.need.phone'),
      F.address === 'required' && !d.addr && tr('wizard.need.address'),
    ],
    [!d.ecName && tr('wizard.need.ecName'), !ecp.ok && tr('wizard.need.ecPhone')],
    child
      // Language is inherited from the account for a child (README §Flows B).
      // A toggled-off question (CMT-2026-08-29-022) is never rendered, so it
      // is never required — the profile completes without it.
      ? [ctx.askShirt !== false && !effShirt && tr('wizard.need.shirt.child')]
      : [
          ctx.askLanguage !== false && !d.language && tr('wizard.need.language'),
          ctx.askShirt !== false && !d.shirt && tr('wizard.need.shirt.self'),
        ],
  ]
  return (byStep[step - 1] || []).filter((x): x is string => Boolean(x))
}

export const stepValid = (step: number, d: WizardDraft, ctx: GateContext): boolean =>
  stepNeeds(step, d, ctx).length === 0

/** Banner template (canon §5): one item → `Add {item} to continue`; several →
 *  `Still needed: {a}, {b} and {c}`. */
export function blockText(needs: string[], tr: TranslateFn = enT): string {
  if (needs.length === 0) return ''
  if (needs.length === 1) return tr('wizard.block.one', { item: needs[0] })
  return tr('wizard.block.many', { list: needs.slice(0, -1).join(', '), last: needs[needs.length - 1] })
}

// ---------------------------------------------------------------------------
// Done screen (canon §5.6)
// ---------------------------------------------------------------------------

/** Profile done-ness is derived from the data itself — never a stored flag
 *  (canon §2.4): first && last && DOB && gender && nationality && residence
 *  && city. `identityLocked` (level <= 2): the identity-core facts count as
 *  present — they were VERIFIED at check-in, which is a stronger claim than a
 *  filled form field, so a locked profile's completeness stands or falls on
 *  the still-editable fields only. */
export const allValid = (
  d: WizardDraft,
  policy: GateContext['genderPolicy'],
  identityLocked = false,
): boolean =>
  (identityLocked || !!(d.first && d.last && dobFull(d) && genderOk(d, policy))) &&
  !!(natSelected(d) && resSelected(d) && cityValid(d))

/** Profile-row completeness for a MANAGED CHILD — derived from the record's
 *  own data, never a stored flag (DC kidRow: dob + gender + nationality +
 *  residence + city; the name always exists on a record). */
export const childProfileComplete = (d: WizardDraft): boolean =>
  !!(dobFull(d) && d.gender && d.nationality && d.resCountry && d.city)

/** `identityLocked`: never lists the locked identity facts — "Still needed:
 *  your name" pointing at a field the user CANNOT edit would be a dead end
 *  (the same count-as-answered rule as stepNeeds/allValid). */
export function missingList(
  d: WizardDraft,
  policy: GateContext['genderPolicy'],
  tr: TranslateFn = enT,
  identityLocked = false,
): string {
  const keys = [
    tr('wizard.missing.name'), tr('wizard.missing.dob'), tr('wizard.missing.gender'),
    tr('wizard.missing.nationality'), tr('wizard.missing.residence'), tr('wizard.missing.city'),
  ]
  const miss = [
    !identityLocked && !(d.first && d.last),
    !identityLocked && !dobFull(d),
    !identityLocked && !genderOk(d, policy),
    !natSelected(d), !resSelected(d), !cityValid(d),
  ]
  return keys.filter((_, i) => miss[i]).join(', ')
}

/** Replace `target`'s IDENTITY-CORE fields with `source`'s — name + DOB +
 *  gender + the gender-adjacent self-describe text (the exact set the wizard
 *  locks at level <= 2; nationality is deliberately excluded — rationale at
 *  `identityLocked` in ProfileWizard.tsx). Used to RE-SYNC a draft's identity
 *  fields from the persisted profile when a lock lands after the draft was
 *  editable (adversarial review P1-1): the locked steps and the receipt must
 *  display only persisted truth, never an in-flight edit or a legacy prefill
 *  that the locked save will omit. */
export function withIdentityFrom(target: WizardDraft, source: WizardDraft): WizardDraft {
  return {
    ...target,
    first: source.first, last: source.last,
    dobD: source.dobD, dobM: source.dobM, dobY: source.dobY,
    gender: source.gender, selfText: source.selfText,
  }
}

/** First incomplete step for `Add these now` / resume (DC `resumeAt` / `pAt`).
 *  `identityLocked`: steps 1–2 are read-only, so resume can never land there
 *  — the first actionable step is 3. */
export function firstIncompleteStep(
  d: WizardDraft,
  policy: GateContext['genderPolicy'],
  identityLocked = false,
): number {
  if (!identityLocked) {
    if (!d.first || !d.last) return 1
    if (!dobFull(d) || !genderOk(d, policy)) return 2
  }
  return 3
}

export interface SummaryRow {
  k: string
  v: string
}

/** Summary card rows in canon order (§5.6); missing values render `—`.
 *  Child subject: phone/language rows drop (never asked of a child); the
 *  junior shirt shows the effective (possibly age-suggested) size.
 *  A toggled-off question (CMT-2026-08-29-022 askLanguage/askShirt=false)
 *  drops its row entirely — a question the event never asked must not show
 *  a `—` gap on the receipt. */
export function summaryRows(d: WizardDraft, ctx: GateContext, tr: TranslateFn = enT): SummaryRow[] {
  const F = ctx.eventFields
  const child = ctx.subject === 'child'
  const effShirt = child ? (d.shirt || ctx.suggestedShirt || '') : d.shirt
  const rows: Array<SummaryRow | null> = [
    { k: tr('wizard.summary.name'), v: `${d.first} ${d.last}`.trim() || '—' },
    { k: tr('wizard.summary.dob'), v: dobFull(d) || '—' },
    { k: tr('wizard.summary.gender'), v: d.gender || '—' },
    d.selfText ? { k: tr('wizard.summary.genderIdentity'), v: d.selfText } : null,
    { k: tr('wizard.summary.nationality'), v: d.nationality || '—' },
    { k: tr('wizard.summary.residence'), v: d.city && d.resCountry ? `${d.city}, ${d.resCountry}` : '—' },
    F.address && d.addr ? { k: tr('wizard.summary.address'), v: d.addr } : null,
    F.phone && !child
      ? { k: tr('wizard.summary.phone'), v: d.phone ? `+${dialOf(d.dial || DEFAULT_DIAL)} ${d.phone}` : tr('wizard.summary.phoneNotGiven') }
      : null,
    { k: tr('wizard.summary.ec'), v: d.ecName ? `${d.ecName}${d.ecRel ? ' · ' + d.ecRel : ''}` : '—' },
    { k: tr('wizard.summary.ecPhone'), v: d.ecPhone ? `+${dialOf(d.ecDial || DEFAULT_DIAL)} ${d.ecPhone}` : '—' },
    child || ctx.askLanguage === false ? null : { k: tr('wizard.summary.language'), v: d.language || '—' },
    ctx.askShirt === false ? null : { k: tr('wizard.summary.shirt'), v: effShirt || '—' },
  ]
  return rows.filter((r): r is SummaryRow => r !== null)
}
